/* eslint-disable react-refresh/only-export-components */
import {
  Outlet,
  Router,
  createRootRoute,
  createRoute,
  redirect,
} from "@tanstack/react-router"
import { Suspense } from "react"
import { Toaster } from "sonner"

import { ThemeProvider } from "./components/theme-provider"
import { CompanyProvider } from "./contexts/company-context"
import { GameModeProvider } from "./contexts/game-mode-context"
import { JobAssignmentsProvider } from "./contexts/job-assignments-context"
import { SearchHistoryProvider } from "./contexts/search-history-context"
import { TasksCommentsProvider } from "./contexts/tasks-comments-context"
import DashboardRoute from "./pages/Dashboard/page"
import AgenticAtsLegacyRedirectPage, {
  AgenticAtsLegacyConversationRedirect,
  AgenticAtsLegacyIndexRedirect,
} from "./pages/agentic-ats-demo/page"
import AuthenticationPage from "./pages/authentication/page"
import NoAccessPage from "./pages/no-access/page"
import CalendarRoute from "./pages/calendar/page"
import CandidatesRoute from "./pages/candidates/page"
import BucketDetailRoute from "./pages/community/[bucketId]/page"
import CommunityViewRoute from "./pages/community/page"
import DesignSystemRoute from "./pages/ds/page"
import DashboardAnalyticsPage from "./pages/jobs/[jobId]/analytics/page"
import JobPageRoute from "./pages/jobs/[jobId]/overview/page"
import JobsPage from "./pages/jobs/page"
import ApprovalsPage from "./pages/ops/approvals/page"
import TasksPageRoute from "./pages/ops/tasks/page"
import PersonDetailsRoute from "./pages/persons/[personId]/page"
import PlacementsPageRoute from "./pages/placements/page"
import SearchPageRoute from "./pages/searches/[searchId]/page"
import NewSearchRoute from "./pages/searches/new/page"
import SearchesRoute from "./pages/searches/page"
import SearchResultRoute from "./pages/searches/results/page"
import SettingsRoute from "./pages/settings/page"
import AgentWorkspaceRoute from "./rootRoutes/AgentWorkspaceRoute"
import AuthenticatedRoute from "./rootRoutes/AuthenticatedRoute"
import MainAppRoute from "./rootRoutes/MainApp"
import SelectOrganizationRoute from "./rootRoutes/SelectOrganizationRoute"

export const rootRoute = createRootRoute({
  component: () => (
    // Graphite is a dark design — the palette only exists in `.dark`. System
    // stays selectable in the theme menu, but a first-time visitor gets the
    // product as designed rather than the pre-Graphite light theme.
    <ThemeProvider
      attribute="class"
      defaultTheme="dark"
      enableSystem
      disableTransitionOnChange
    >
      <Suspense fallback={null}>
        <JobAssignmentsProvider>
          <SearchHistoryProvider>
            <CompanyProvider>
              <TasksCommentsProvider>
                <GameModeProvider>
                  <Outlet />
                </GameModeProvider>
              </TasksCommentsProvider>
            </CompanyProvider>
          </SearchHistoryProvider>
        </JobAssignmentsProvider>
        <Toaster expand />
      </Suspense>
    </ThemeProvider>
  ),
})

const EmptyRoute = () => null

const RootIndexRoute = createRoute({
  getParentRoute: () => AuthenticatedRoute,
  beforeLoad: () => {
    throw redirect({ to: "/dashboard", replace: true })
  },
  component: EmptyRoute,
  path: "/",
})

const defaultOpsRoute = createRoute({
  getParentRoute: () => MainAppRoute,
  beforeLoad: () => {
    throw redirect({ to: "/ops/tasks", replace: true })
  },
  component: EmptyRoute,
  path: "/ops",
})

const defaultBucketsRoute = createRoute({
  getParentRoute: () => MainAppRoute,
  beforeLoad: () => {
    throw redirect({ to: "/community", replace: true })
  },
  component: EmptyRoute,
  path: "/buckets",
})

const defaultPersonRoute = createRoute({
  getParentRoute: () => MainAppRoute,
  beforeLoad: () => {
    throw redirect({ to: "/community", replace: true })
  },
  component: EmptyRoute,
  path: "/persons",
})

const CommunityIndexRoute = createRoute({
  getParentRoute: () => MainAppRoute,
  path: "/community",
  beforeLoad: () => {
    throw redirect({ to: "/community/companies", replace: true })
  },
  component: EmptyRoute,
})

const routeTree = rootRoute.addChildren([
  AuthenticationPage,
  NoAccessPage,
  AuthenticatedRoute.addChildren([
    RootIndexRoute,
    SelectOrganizationRoute,
    AgenticAtsLegacyRedirectPage.addChildren([
      AgenticAtsLegacyIndexRedirect,
      AgenticAtsLegacyConversationRedirect,
    ]),
    AgentWorkspaceRoute.addChildren([
      MainAppRoute.addChildren([
        CalendarRoute,
        DashboardRoute,
        DashboardAnalyticsPage,
        JobsPage,
        JobPageRoute,
        SearchesRoute,
        NewSearchRoute,
        SearchPageRoute,
        SearchResultRoute,
        CandidatesRoute,
        PlacementsPageRoute,
        CommunityIndexRoute,
        CommunityViewRoute,
        DesignSystemRoute,
        defaultOpsRoute,
        TasksPageRoute,
        ApprovalsPage,
        SettingsRoute,
        defaultBucketsRoute,
        BucketDetailRoute,
        defaultPersonRoute,
        PersonDetailsRoute,
      ]),
    ]),
  ]),
])

export const router = new Router({ routeTree })
